<!--
 * @Author       : 花裤衩<白永明><yongming.bai@outlook.com>
 * @Date         : 2026-01-13 10:10:56 +0800
 * @LastEditors  : 花裤衩<白永明><yongming.bai@outlook.com>
 * @LastEditTime : 2026-01-14 09:54:51 +0800
 * @FilePath     : /ai-agent-lumina/src/components/global/scroll-content/src/izt-scroll-content.vue
 * @Description  : 滚动容器
 * Copyright 2026 - 2026 iZT Team. All Rights Reserved.
 * 属性：
 * direction       vertical(垂直) | horizontal(水平)     默认vertical
 * fade-size       渐隐尺寸(rem)                         默认0.32  (24px)
 * class-name      容器类名                              默认空400px 主要用于控制容器高度使用
-->
<style lang="scss" scoped>
  .izt-scroll-content-container {
    position: relative;
    transition: mask-image 0.2s ease;
    .main-content {
      width: 100%;
    }

    /* 垂直方向的默认样式 */
    &.vertical {
      width: auto;
      overflow-y: auto;
      height: 400px;
      /* 默认垂直渐隐样式 */
      -webkit-mask-image: linear-gradient(to bottom,
        var(--izt-theme-color) 0,
        var(--izt-theme-color) calc(100% - 24px),
        var(--izt-theme-color) 100%
      );
      mask-image: linear-gradient(to bottom,
        var(--izt-theme-color) 0,
        var(--izt-theme-color) calc(100% - 24px),
        var(--izt-theme-color) 100%
      );
    }

    /* 水平方向的默认样式 */
    &.horizontal {
      width: 100%;
      height: auto;
      overflow-x: auto;
      white-space: nowrap; /* 防止内容换行 */
      /* 默认水平渐隐样式 */
      -webkit-mask-image: linear-gradient(to right,
        var(--izt-theme-color) 0,
        var(--izt-theme-color) calc(100% - 24px),
        var(--izt-theme-color) 100%
      );
      mask-image: linear-gradient(to right,
        var(--izt-theme-color) 0,
        var(--izt-theme-color) calc(100% - 24px),
        var(--izt-theme-color) 100%
      );
    }
  }

  /* 内容容器样式 */
  .izt-scroll-content-container > div {
    &.horizontal {
      display: inline-block;
      min-width: 100%;
    }
  }
</style>

<template>
  <div :class="['izt-scroll-content-container overflow-style', direction, className]" ref="containerRef" :style="maskStyle">
    <div ref="contentRef" :class="['main-content', direction]">
      <slot />
    </div>
  </div>
</template>

<script setup name="izt-scroll-content">
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';

defineOptions({ name: 'izt-scroll-content' })

const props = defineProps({
  direction: {
    type: String,
    default: 'vertical', // vertical(垂直) | horizontal(水平)
    validator: (value) => ['vertical', 'horizontal'].includes(value)
  },
  className: {
    type: String,
    default: ''
  },
  // 渐隐区域尺寸
  fadeSize: {
    type: String,
    default: '0.32rem'
  }
})

const containerRef = ref(null);
const contentRef = ref(null);
const showStartMask = ref(false); // 开始位置渐隐（垂直时为顶部，水平时为左侧）
const showEndMask = ref(false);   // 结束位置渐隐（垂直时为底部，水平时为右侧）

// 检查滚动状态
const checkScroll = () => {
  if (!containerRef.value || !contentRef.value) return;

  const container = containerRef.value;
  const content = contentRef.value;
  if (props.direction === 'vertical') {
    // 垂直方向：检查顶部和底部
    showStartMask.value = container.scrollTop > 0;
    const isBottom = container.scrollTop + container.clientHeight >= (content.scrollHeight - 2); // 2像素偏差
    showEndMask.value = !isBottom;
  } else {
    // 水平方向：检查左侧和右侧
    showStartMask.value = container.scrollLeft > 0;
    const isRight = container.scrollLeft + container.clientWidth >= content.scrollWidth;
    showEndMask.value = !isRight;
  }
};

// 计算动态mask样式
const maskStyle = computed(() => {
  if (props.direction === 'vertical') {
    // 垂直方向渐隐
    let stops = [];

    // 顶部渐隐
    if (showStartMask.value) {
      stops.push('transparent 0');
      stops.push(`var(--izt-theme-color) ${props.fadeSize}`);
    } else {
      stops.push('var(--izt-theme-color) 0');
    }

    // 中间部分
    stops.push(`var(--izt-theme-color) calc(100% - ${props.fadeSize})`);

    // 底部渐隐
    if (showEndMask.value) {
      stops.push('transparent 100%');
    } else {
      stops.push('var(--izt-theme-color) 100%');
    }

    return {
      'mask-image': `linear-gradient(to bottom, ${stops.join(', ')})`,
      '-webkit-mask-image': `linear-gradient(to bottom, ${stops.join(', ')})`
    };
  } else {
    // 水平方向渐隐
    let stops = [];

    // 左侧渐隐
    if (showStartMask.value) {
      stops.push('transparent 0');
      stops.push(`var(--izt-theme-color) ${props.fadeSize}`);
    } else {
      stops.push('var(--izt-theme-color) 0');
    }

    // 中间部分
    stops.push(`var(--izt-theme-color) calc(100% - ${props.fadeSize})`);

    // 右侧渐隐
    if (showEndMask.value) {
      stops.push('transparent 100%');
    } else {
      stops.push('var(--izt-theme-color) 100%');
    }

    return {
      'mask-image': `linear-gradient(to right, ${stops.join(', ')})`,
      '-webkit-mask-image': `linear-gradient(to right, ${stops.join(', ')})`
    };
  }
});

// 添加滚动监听
onMounted(() => {
  if (containerRef.value) {
    containerRef.value.addEventListener('scroll', checkScroll);
    // 初始检查一次
    checkScroll();
  }
});

// 移除滚动监听
onUnmounted(() => {
  if (containerRef.value) {
    containerRef.value.removeEventListener('scroll', checkScroll);
  }
});

// 监听内容变化
const observer = new ResizeObserver(() => {
  checkScroll();
});

onMounted(() => {
  if (contentRef.value) {
    observer.observe(contentRef.value);
  }
});

onUnmounted(() => {
  observer.disconnect();
});

// 监听方向变化
watch(() => props.direction, () => {
  // 方向变化时重置滚动位置
  if (containerRef.value) {
    containerRef.value.scrollTop = 0;
    containerRef.value.scrollLeft = 0;
  }
  // 重新检查滚动状态
  setTimeout(checkScroll, 0);
});

// 暴露方法给父组件
defineExpose({
  checkScroll,
  containerRef,
  contentRef
});
</script>